import * as z from 'zod';

import {
  capturedImageSchema,
  marker2dCoordinateSchema,
  marker3dCoordinateSchema,
} from '../engine/slot-values';
import type {
  FormTemplateDocument,
  WidgetField,
} from '../model/form-template-document.types';
import { isGroupField, isWidgetField } from '../model/form-field';
import {
  RATING_VALUES,
  WIDGET_SLOTS,
  YES_NO_UNKNOWN_VALUES,
} from '../model/widget-slots';

/**
 * Generates the structural Zod schema for the submission blob `{ data, outputs }`
 * a template produces. Every bound slot yields a per-slot-type leaf fragment;
 * every placed widget's outputs land under `outputs.<field id>`.
 *
 * The schema is structural only: all leaf paths are optional (requiredness is
 * the submit-time evaluator's job) and every object is strict (unknown keys
 * rejected).
 */
export function generateSubmissionSchema(
  document: FormTemplateDocument,
): z.ZodType {
  const dataRoot: BranchNode = { kind: 'branch', children: new Map() };
  const outputsRoot: BranchNode = { kind: 'branch', children: new Map() };

  for (const field of eachWidgetField(document)) {
    if (field.bind) {
      for (const [slot, target] of Object.entries(field.bind)) {
        insert(dataRoot, target.split('.'), leafSchemaFor(field, slot));
      }
    }
    for (const output of WIDGET_SLOTS[field.widget].outputs) {
      insert(outputsRoot, [field.id, output], outputLeafSchemaFor(output));
    }
  }

  return z.strictObject({
    data: compile(dataRoot).optional(),
    outputs: compile(outputsRoot).optional(),
  });
}

type Node = { kind: 'leaf'; schema: z.ZodType } | BranchNode;
interface BranchNode {
  kind: 'branch';
  children: Map<string, Node>;
}

function* eachWidgetField(
  document: FormTemplateDocument,
): Generator<WidgetField> {
  for (const section of document.sections) {
    for (const field of section.fields) {
      if (isGroupField(field)) {
        yield* field.group.fields;
        continue;
      }
      if (isWidgetField(field)) {
        yield field;
      }
    }
  }
}

function insert(root: BranchNode, segments: string[], schema: z.ZodType): void {
  let node = root;
  for (let i = 0; i < segments.length - 1; i++) {
    const key = segments[i];
    let child = node.children.get(key);
    if (!child) {
      child = { kind: 'branch', children: new Map() };
      node.children.set(key, child);
    }
    if (child.kind !== 'branch') {
      throw new Error(`Bind path segment "${key}" is both a leaf and a branch`);
    }
    node = child;
  }
  node.children.set(segments[segments.length - 1], { kind: 'leaf', schema });
}

function compile(node: Node): z.ZodType {
  if (node.kind === 'leaf') {
    return node.schema;
  }
  const shape: Record<string, z.ZodType> = {};
  for (const [key, child] of node.children) {
    shape[key] = compile(child).optional();
  }
  return z.strictObject(shape);
}

/**
 * Leaf schema per output name. An output is machine-read, so it gets the
 * strictest leaf its meaning allows rather than a bare `z.string()`.
 */
const OUTPUT_LEAF_SCHEMAS: Record<string, z.ZodType> = {
  isMelanoma: z.boolean(),
};

function outputLeafSchemaFor(output: string): z.ZodType {
  const schema = OUTPUT_LEAF_SCHEMAS[output];
  if (!schema) {
    throw new Error(`Output "${output}" has no declared leaf schema`);
  }
  return schema;
}

/**
 * A slot holding chosen ids: the same id twice says nothing the single id does
 * not, so the shape gate rejects it rather than persisting a clinical answer
 * that reads as two answers. The renderers toggle selections and cannot produce
 * one; a hand-built blob can.
 */
const uniqueIdArray = z
  .array(z.string())
  .refine((values) => new Set(values).size === values.length, {
    message: 'Repeats a selected value',
  });

function leafSchemaFor(field: WidgetField, slot: string): z.ZodType {
  switch (field.widget) {
    case 'yes-no-unknown':
      return field.params?.allowUnknown === false
        ? z.enum(YES_NO_UNKNOWN_VALUES).extract(['yes', 'no'])
        : z.enum(YES_NO_UNKNOWN_VALUES);
    case 'rating':
      return z.enum(RATING_VALUES);
    case 'single-select':
    case 'scale':
    case 'skin-type':
    case 'diagnosis-tree':
    case 'diagnosis-search':
      return z.string();
    case 'multi-select':
      return slot === 'selected' ? uniqueIdArray : z.string();
    case 'text':
    case 'note':
      return z.string();
    case 'number': {
      let schema = z.int();
      if (field.params?.min !== undefined)
        schema = schema.min(field.params.min);
      if (field.params?.max !== undefined)
        schema = schema.max(field.params.max);
      return schema;
    }
    case 'location-picker':
      return field.params.pickerType === '3d'
        ? marker3dCoordinateSchema
        : marker2dCoordinateSchema;
    case 'image-capture':
      return z.array(capturedImageSchema);
    case 'clinical-action':
      return z.string();
  }
}
